import { z } from "zod";

import { CUE_ERROR_KINDS } from "../domain/chat/ordered-part.schema";
import { ChatStreamTokenUsageSchema } from "./stream-usage.types";

/**
 * Stable machine-readable error codes consumed by `ErrorEventSchema.code`.
 * Use SCREAMING_SNAKE_CASE. These values must not change once published —
 * frontend consumers switch/case on them.
 */
export const STREAM_ERROR_CODES = {
  NEEDS_API_KEY: "NEEDS_API_KEY",
  MESSAGE_REQUIRED: "MESSAGE_REQUIRED",
  CONVERSATION_NOT_FOUND: "CONVERSATION_NOT_FOUND",
  STREAM_PERSISTENCE_FAILED: "STREAM_PERSISTENCE_FAILED",
  TOOL_UNSUPPORTED_TYPE: "TOOL_UNSUPPORTED_TYPE",
  TOOL_INVALID_CALL: "TOOL_INVALID_CALL",
  TOOL_UNKNOWN: "TOOL_UNKNOWN",
  PROVIDER_ERROR: "PROVIDER_ERROR",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  INTERNAL_STREAM_ERROR: "INTERNAL_STREAM_ERROR",
  ABORTED: "ABORTED",
  RATE_LIMITED: "RATE_LIMITED",
  PROVIDER_OVERLOADED: "PROVIDER_OVERLOADED",
  TIMEOUT: "TIMEOUT",
  PARSE_ERROR: "PARSE_ERROR",
  INTERNAL: "INTERNAL",
  STREAM_ALREADY_IN_PROGRESS: "STREAM_ALREADY_IN_PROGRESS",
  // An image attached to a Cue turn could not be read into the bytes the agent is handed: not an
  // object uploaded into this conversation, too large, missing, or not an image. The turn is
  // refused before anything is written for it. category=validation, action=fix_request.
  IMAGE_REFUSED: "IMAGE_REFUSED",
  // Sandbox-provisioning failures. Used by surfaces that depend on a Daytona
  // sandbox (Cue) to give the frontend a typed signal instead of the
  // generic "An error occurred" copy.
  //   SANDBOX_PROVISIONING_FAILED → Daytona rejected the create call
  //                                 (auth, rate-limit, infra). category=upstream, action=contact_support.
  //   SANDBOX_TIMEOUT             → the sandbox accepted but never reached
  //                                 the started state within the budget.
  //                                 category=upstream, action=contact_support.
  //   Both are classified by `classifyDaytonaError` in the Daytona create-stream
  //   service, and every site that emits its result hardcodes that category and
  //   that action — so neither code carries `retryAfterMs`.
  //   INFRASTRUCTURE_DEGRADED     → the workspace is not usable for this turn. TWO emitters,
  //                                 both in the Cue stream orchestrator and both
  //                                 category=unavailable; the action is what separates them.
  //   Snapshot probe could not answer — an INDETERMINATE resolution, never an absent snapshot,
  //   which is not this code at all and takes the INTERNAL_ERROR / contact_support path. The turn
  //   is refused before it runs and the verdict is never cached, so the next send re-probes; the
  //   message asks the user to send again, so action=none — terminal, and no `retryAfterMs`
  //   (NEX-3941). The argument for `none` over `wait_and_retry` is recorded at that emit site,
  //   beside `snapshotUnavailabilityIsTransient`.
  //   Cross-pod hand-off could not place the turn's attachments in the workspace — carried on
  //   the hand-off descriptor's `refusal` and sent by the one tail, not emitted inline. A
  //   specific thing has to clear before a resend can land (the owning instance finishing its
  //   turn, or a transient sandbox-platform failure), so action=wait_and_retry carries that
  //   wait as `retryAfterMs` — the only one of the two that does.
  SANDBOX_PROVISIONING_FAILED: "SANDBOX_PROVISIONING_FAILED",
  SANDBOX_TIMEOUT: "SANDBOX_TIMEOUT",
  INFRASTRUCTURE_DEGRADED: "INFRASTRUCTURE_DEGRADED"
} as const;

export type StreamErrorCode = (typeof STREAM_ERROR_CODES)[keyof typeof STREAM_ERROR_CODES];

/**
 * Classifies the error into a small closed set of families.
 *
 * Rules for choosing a category:
 *  - Use `auth` when credentials are absent or invalid — the request cannot proceed as-is.
 *  - Use `permission` when credentials are valid but the user lacks access.
 *  - Use `validation` when the request shape or content is rejected by the server.
 *  - Use `not_found` when an addressed resource does not exist.
 *  - Use `rate_limit` when the server is throttling the caller.
 *  - Use `precondition` when a required prior state is not met (e.g. quota exhausted, plan limit, feature flag off).
 *  - Use `upstream` when a third-party provider (OpenAI, Anthropic, Daytona) returns an error.
 *  - Use `timeout` when a deadline was exceeded on either the provider call or DB write.
 *  - Use `unavailable` when the service is transiently down — distinct from `upstream` because the fix is server-side.
 *  - Use `internal` for all unhandled exceptions / programming bugs. Default when nothing else fits.
 *
 * Inspired by Stripe's six error types and gRPC's canonical status codes.
 * Maps cleanly to HTTP families: 401→auth, 403→permission, 400→validation,
 * 404→not_found, 429→rate_limit, 412→precondition, 502/503→upstream/unavailable,
 * 504→timeout, 500→internal.
 */
export const StreamErrorCategorySchema = z.enum([
  "auth", // Missing/invalid/expired API key or session token.
  //   Example: NEEDS_API_KEY in cue-stream-orchestrator.service.ts
  "permission", // Authenticated but not authorized for this resource.
  //   Example: org-level feature flag blocks access
  "validation", // Request content is structurally or semantically wrong.
  //   Example: empty message body in cue.service.ts
  "not_found", // Addressed resource (conversation, agent) does not exist.
  //   Example: "Conversation not found." in cue.service.ts
  "rate_limit", // Caller is throttled. Pair with retryAfterMs when known.
  //   Example: OpenAI 429 surfaced via sanitizeErrorMessage
  "precondition", // A required prerequisite is not met.
  //   Example: model quota exceeded, plan tier mismatch
  "upstream", // External AI provider or sandbox returned an error.
  //   Example: OpenAI/Anthropic/Daytona adapter catch blocks
  "timeout", // Deadline exceeded — provider call or DB write took too long.
  //   Example: Daytona sandbox startup timeout
  "unavailable", // Transient infrastructure failure, distinct from upstream.
  //   Example: Redis unreachable, streaming infra overloaded
  "internal" // Unhandled exception / programming bug — default fallback.
  //   Example: sanitizeSseError(error) catch paths in controllers
]);

export type StreamErrorCategory = z.infer<typeof StreamErrorCategorySchema>;

/**
 * Tells the client exactly what to do when it receives an error event.
 * This is the primary dispatch axis in the frontend — `switch (event.action)`.
 *
 * Rules for choosing an action:
 *  - `reauthenticate` — only when the user can supply fresh credentials right now.
 *  - `fix_request`    — only when the user made a correctable mistake (bad input, missing field).
 *  - `retry`          — transient error, client should retry immediately with no user interaction.
 *  - `wait_and_retry` — transient but needs backoff; `retryAfterMs` MUST be set alongside this action.
 *  - `contact_support` — terminal, problem is on our side, user cannot fix it.
 *  - `none`           — terminal, stream ended, nothing to do (e.g. abort path that also emits error).
 *
 * Explicit `action` replaces the boolean `recoverable`:
 *  - Old `recoverable: true`  → `retry` or `wait_and_retry`
 *  - Old `recoverable: false` → `reauthenticate`, `fix_request`, `contact_support`, or `none`
 *
 * Mapping is not 1:1 — `action` carries the UI directive that `recoverable` could not express.
 */
export const StreamErrorActionSchema = z.enum([
  "reauthenticate", // User must re-enter credentials / provision API key.
  //   Example: NEEDS_API_KEY → open API key provisioning dialog
  "fix_request", // User must correct their input and resubmit.
  //   Example: empty message body → show validation toast
  "retry", // Client should retry immediately — no user action needed.
  //   Example: tool-call parse error mid-stream → orchestrator continues
  "wait_and_retry", // Client should retry after retryAfterMs — no user action needed.
  //   Example: provider rate-limit with Retry-After header
  "contact_support", // Terminal — show support link with correlationId.
  //   Example: unhandled internal exception in orchestrator
  "none" // Terminal — stream ended, surface error message only.
  //   Example: DB persistence failure after successful stream
]);

export type StreamErrorAction = z.infer<typeof StreamErrorActionSchema>;

/**
 * Emitted in-band over SSE when the stream encounters an error.
 *
 * ## Design philosophy: three required fields, four optional enrichment fields
 *
 * ```
 * category  →  family of error (routing, metrics, logging)
 * code      →  stable machine identifier (switch/case in downstream logic)
 * action    →  what the client should do (drives UI declaratively)
 * ```
 *
 * This mirrors RFC 9457 (type URI = class of problem), Stripe (type + code + doc_url),
 * and gRPC `google.rpc.Status` (code enum + structured details). The key addition over
 * those standards is the explicit `action` field — no consumer needs to derive client
 * behavior from `category` or `code` alone.
 *
 * ## Interaction with the discriminated union
 *
 * `ErrorEventSchema` participates in `ChatStreamEventSchema` via `z.discriminatedUnion("type", [...])`.
 * The `type: z.literal("error")` discriminant MUST remain unchanged. The union narrows correctly
 * even with nested enum schemas because `z.enum` produces a literal union at the TypeScript level.
 *
 * ## Usage examples
 *
 * ### Good — all required fields, meaningful enrichment
 * ```ts
 * onEvent({
 *   type: "error",
 *   category: "auth",
 *   code: "NEEDS_API_KEY",
 *   message: "An API key is required to run Cue.",
 *   action: "reauthenticate",
 *   correlationId: requestId,
 *   docUrl: "https://docs.nexus.ai/cue/api-keys"
 * });
 * ```
 *
 * ### Bad — omitting required fields, using free-form message for routing
 * ```ts
 * // WRONG: code omitted, action missing, frontend forced to check message string
 * onEvent({ type: "error", message: "API key required", recoverable: false });
 * ```
 *
 * ## Optional field guidance
 *
 * - `retryAfterMs`  — set ONLY when `action` is `wait_and_retry`. Omit otherwise.
 *                     Value is milliseconds. Source: provider Retry-After header × 1000,
 *                     or a fixed backoff (e.g. 5000ms for rate-limited upstreams).
 * - `correlationId` — set whenever a trace/request ID is available in scope.
 *                     Source: `X-Request-Id` header, Sentry event ID, or NestJS logger
 *                     correlation context. Allows support to correlate frontend error
 *                     with backend log line.
 * - `docUrl`        — set for user-actionable errors where a docs page exists.
 *                     Format: absolute HTTPS URL. Omit for internal/transient errors.
 * - `details`       — structured extension bag for machine-readable extra context.
 *                     Keys are camelCase strings. Values are JSON-serializable unknowns.
 *                     Example: `{ fieldName: "message", constraint: "minLength(1)" }`
 *                     for validation errors. NEVER put PII here — the SSE sanitizer
 *                     (`redactSensitiveContent`) operates on the serialized JSON string
 *                     and will redact API key patterns, but it cannot redact arbitrary PII.
 *                     When in doubt, omit.
 *
 * ## Cookbook — copy-paste patterns for common error emissions
 *
 * ```ts
 * // 1. Auth — API key missing (Cue)
 * const AUTH_NEEDS_KEY: StreamErrorEvent = {
 *   type: "error", category: "auth", code: "NEEDS_API_KEY",
 *   message: "An API key is required to run Cue.",
 *   action: "reauthenticate"
 * };
 *
 * // 2. Rate limit — provider throttled (include retryAfterMs when known)
 * const RATE_LIMITED: StreamErrorEvent = {
 *   type: "error", category: "rate_limit", code: "PROVIDER_RATE_LIMITED",
 *   message: "The AI provider is rate-limiting this request. Retrying shortly.",
 *   action: "wait_and_retry", retryAfterMs: 5000
 * };
 *
 * // 3. Validation — empty message body
 * const EMPTY_MESSAGE: StreamErrorEvent = {
 *   type: "error", category: "validation", code: "MESSAGE_REQUIRED",
 *   message: "Message content is required.", action: "fix_request"
 * };
 *
 * // 4. Upstream provider failure
 * const UPSTREAM_FAIL: StreamErrorEvent = {
 *   type: "error", category: "upstream", code: "PROVIDER_ERROR",
 *   message: sanitizedProviderMessage, action: "contact_support",
 *   correlationId: requestId
 * };
 *
 * // 5. Internal / unhandled exception
 * const INTERNAL: StreamErrorEvent = {
 *   type: "error", category: "internal", code: "INTERNAL_ERROR",
 *   message: "An unexpected error occurred. Our team has been notified.",
 *   action: "contact_support", correlationId: sentryEventId
 * };
 * ```
 */
export const ErrorEventSchema = z.object({
  /** Discriminant — must remain `"error"` for the discriminated union to work. */
  type: z.literal("error"),

  /**
   * Family of error. Use for logging, metrics, and coarse-grained client routing.
   * Drives switch/case only when `action` alone is insufficient (rare).
   */
  category: StreamErrorCategorySchema,

  /**
   * Stable machine-readable error identifier within the category.
   * Required (was optional in v1 — that was a mistake). Use SCREAMING_SNAKE_CASE.
   * Must not change once published — consumers switch/case on it.
   * Examples: "NEEDS_API_KEY", "MESSAGE_REQUIRED", "CONVERSATION_NOT_FOUND",
   *           "PROVIDER_ERROR", "INTERNAL_ERROR", "STREAM_PERSISTENCE_FAILED"
   */
  code: z.enum(Object.values(STREAM_ERROR_CODES) as [StreamErrorCode, ...StreamErrorCode[]]),

  /** Human-readable explanation of this specific occurrence. Sanitized before emission. */
  message: z.string(),

  /**
   * Client directive — the primary dispatch axis in frontend error handling.
   * Frontend code must switch on this field, not on `code` or `message`.
   */
  action: StreamErrorActionSchema,

  /**
   * How long the client should wait before retrying, in milliseconds.
   * MUST be set when `action === "wait_and_retry"`. Ignored otherwise.
   * Source: provider Retry-After header value × 1000, or a fixed backoff constant.
   */
  retryAfterMs: z.number().int().nonnegative().optional(),

  /**
   * Opaque trace/request identifier for support correlation.
   * Set whenever a request ID, Sentry event ID, or NestJS logger correlation
   * context is available. Omit if none is in scope — do not fabricate.
   */
  correlationId: z.string().optional(),

  /**
   * Absolute HTTPS URL to a documentation page for this error code.
   * Set only for user-actionable errors where a public docs page exists.
   * Omit for internal/transient errors. Format: z.string().url() enforced at runtime.
   */
  docUrl: z.string().url().optional(),

  /**
   * Structured extension bag for machine-readable extra context.
   * Keys: camelCase strings. Values: JSON-serializable, no PII.
   * Examples:
   *   validation → { fieldName: "message", constraint: "minLength(1)" }
   *   tool error → { toolName: "modify_prompt", rawArgs: "..." }
   */
  details: z.record(z.string(), z.unknown()).optional(),

  /**
   * Token usage accumulated up to the point of failure.
   * Carried by provider adapters on non-abort error paths so billing and tracing
   * middleware receive usage without needing a preceding message-end event.
   */
  usage: ChatStreamTokenUsageSchema.optional(),

  /**
   * Wall-clock ms spent in thinking blocks up to the point of failure.
   * Carried by provider adapters on non-abort error paths.
   */
  totalReasoningDurationMs: z.number().optional(),

  /**
   * Provider-assigned request identifier for support correlation.
   * Carried by provider adapters on non-abort error paths.
   */
  providerRequestId: z.string().optional(),

  /**
   * Which failure this is, in the transcript's own vocabulary rather than the
   * provider's. Classified at the sandbox host, where the provider's fields are
   * still in hand; nothing downstream re-derives it, because nothing
   * downstream holds the inputs.
   *
   * Present on every failure that ended a generation through the runner's
   * Error IPC — the provider's own, or the runner's own as `runner_fault`.
   * Absent on a validation error, a withheld turn, an infrastructure notice.
   * Its absence is what tells the turn accumulator this error is not one to
   * record on the row.
   */
  errorKind: z.enum(CUE_ERROR_KINDS).optional(),

  /**
   * The provider's own account of why the turn ended, verbatim. A fault the
   * runner raised for itself carries none.
   *
   * Declared rather than left in `details`: it is the provenance behind
   * `errorKind`, it is persisted on the turn, and a value read out of an
   * untyped bag is a value no reader can rely on being there.
   */
  terminalReason: z.string().max(64).optional(),

  /** The ending's own result-error subtype, verbatim. Provenance, as above. */
  resultSubtype: z.string().max(64).optional(),

  /**
   * The HTTP status behind an upstream failure.
   *
   * Declared here and deliberately NOT carried onto the persisted turn: it is
   * read at the host to classify, and every durable copy a reader might want
   * already exists on the trace row and the error-tracker tags. Nothing in the
   * transcript acts on it.
   */
  apiErrorStatus: z.number().int().optional(),

  /**
   * Set when this failure ended a paused turn's own generation — the lead's,
   * still running after a hold closed its turn's segment. Such a failure
   * arrives with no turn-start of its own, yet it belongs to that turn rather
   * than to work the lead started between turns.
   *
   * One that wrote first lands on a promoted row whose persisted error part
   * already reads as the turn's. One with nothing written after the pause is
   * also stamped `failedBeforeWriting`, and the row of its own it becomes
   * keeps this field, so a reload reads that row as the turn's too.
   */
  endsPausedTurn: z.literal(true).optional(),

  /**
   * Whether the generation this failure ended wrote nothing a composed row
   * will carry, so no turn records it. The between-turns host that appended
   * this event records it as a row of its own, keyed by the log entry this
   * event rides, and a reader draws that row rather than a banner.
   *
   * Present only when true; its absence claims nothing — a failure on the
   * turn path is not stamped, because a turn's own settle already composes
   * the row this event lands on. Set by the between-turns consumer alone.
   */
  failedBeforeWriting: z.literal(true).optional()
});
